import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from 'primitive-components';
import { colors, fonts } from 'bench-style-constants';

type ButtonProps = {
  children: ReactNode;
  iconLeft?: string;
  iconRight?: string;
  size?: 'small' | 'medium' | 'large';
  subtle?: boolean;
  styleType?: 'default' | 'primary' | 'secondary' | 'neutral' | 'danger' | 'white';
  type?: 'button' | 'submit' | 'reset';
};

const StyledButton = styled.button<ButtonProps>`
  display: flex;
  border: 0;
  border-radius: 0.25rem;
  padding: 8px 16px;
  background: ${colors.shell};
  justify-content: center;
  font-family: ${fonts.circular};
  font-size: 16px;
  font-weight: 300;
  line-height: 24px;
  transition: all 0.25s ease-in-out;
  color: ${colors.water};

  svg {
    fill: ${colors.water};
    transition: fill 0.25s ease-in;
  }

  &:hover {
    color: ${colors.creek};
    cursor: pointer;

    svg {
      fill: ${colors.creek};
    }
  }

  &[disabled] {
    color: ${colors.smoke};
    opacity: 0.32;
    cursor: default;

    svg {
      fill: ${colors.smoke};
    }
  }

  ${props =>
    props.size === 'small' &&
    css`
      padding: 12px 16px;
      font-size: 12px;
      line-height: 16px;
    `}

  ${props =>
    props.size === 'large' &&
    css`
      padding: 16px 24px;
      font-size: 20px;
      font-weight: 600;
      line-height: 32px;
    `}

  ${props =>
    props.styleType === 'primary' &&
    css`
      background: ${colors.water};
      color: ${colors.white};

      svg {
        fill: ${colors.white};
      }

      &:hover {
        background: ${colors.creek};
        color: ${colors.white};

        svg {
          fill: ${colors.white};
        }
      }
    `}

  ${props =>
    props.styleType === 'secondary' &&
    css`
      background: transparent;
      box-shadow: inset 0 0 0 1px ${colors.clay};
    `}

  ${props =>
    props.styleType === 'danger' &&
    css`
      background: ${colors.smoke};
      color: ${colors.white};

      svg {
        fill: ${colors.white};
      }

      &:hover {
        background: ${colors.copper};
        color: ${colors.white};

        svg {
          fill: ${colors.white};
        }
      }
    `}

  ${props =>
    props.subtle &&
    css`
      padding-left: 0;
      padding-right: 0;
      background: transparent;

      &:hover,
      &:focus {
        background: transparent;
      }

      ${props.styleType === 'primary' &&
      css`
        color: ${colors.water};

        svg {
          fill: ${colors.water};
        }

        &:hover,
        &:focus {
          color: ${colors.creek};

          svg {
            fill: ${colors.creek};
          }
        }
      `}

      ${props.styleType === 'neutral' &&
      css`
        color: ${colors.smoke};

        svg {
          fill: ${colors.smoke};
        }

        &:hover,
        &:focus {
          color: ${colors.charcoal};

          svg {
            fill: ${colors.charcoal};
          }
        }
      `}

    ${props.styleType === 'danger' &&
      css`
        color: ${colors.smoke};

        svg {
          fill: ${colors.smoke};
        }

        &:hover,
        &:focus {
          color: ${colors.copper};

          svg {
            fill: ${colors.copper};
          }
        }
      `}
    ${props.styleType === 'white' &&
      css`
        color: ${colors.white};

        svg {
          fill: ${colors.white};
        }

        &:hover,
        &:focus {
          color: ${colors.white};

          svg {
            fill: ${colors.white};
          }
        }
      `}
    `}
`;

const Button = (props: ButtonProps & React.HTMLProps<HTMLButtonElement>) => (
  <StyledButton {...props}>
    {props.iconLeft && <Icon icon={props.iconLeft} size={props.size} margin="right" />}
    {props.children}
    {props.iconRight && <Icon icon={props.iconRight} size={props.size} margin="left" />}
  </StyledButton>
);

Button.defaultProps = {
  iconLeft: '',
  iconRight: '',
  size: 'medium',
  subtle: false,
  styleType: 'default',
  type: 'submit',
};
export default Button;
